import React, { useEffect, useState } from "react";
import { SendFill, Eye } from "react-bootstrap-icons";
import { Outlet, useNavigate } from "react-router-dom";
import ModalWrapper from "../../../../components/learning-development/common/ModalWrapper";

import DynamicTable from "../../../../components/learning-development/common/DynamicTable";
import api from "../../../../Axios";
import { toast } from "react-toastify";
import { defaultDateFormatter, stripHtml } from "../../../../Helper/functionHelper";
import CommonFilter from "../../../../components/common/CommonFilter";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import useLoader from "../../../../hooks/useLoader";
import { Tooltip } from "@mui/material";
import useModal from "../../../../hooks/useModal";
import { TextField, Rating, Box, Button } from "@mui/material";
import UploadIcon from '@mui/icons-material/Upload';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}`;

const FeedbackForm = ({ programId, onSubmit, handleUpdate }) => {

  const [rating, setRating] = useState(0);
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.patch("/lnd/send-feedback", {
        id: programId,
        rating,
        description,
      });
      toast.success("Feedback submitted");
      if (onSubmit) onSubmit();
      if (handleUpdate) handleUpdate();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ minWidth: 320 }}>
      <Box mb={2}>
        <div className="fw-bold mb-2">Your Rating</div>
        <Rating
          value={rating}
          onChange={(_, val) => setRating(val)}
          size="large"
        />
      </Box>
      <Box mb={2}>
        <div className="fw-bold mb-2">Description</div>
        <TextField
          value={description}
          onChange={e => setDescription(e.target.value)}
          multiline
          minRows={3}
          fullWidth
          placeholder="Write your feedback..."
        />
      </Box>
      <div className="d-flex justify-content-end gap-2 mt-3">
        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={submitting || !rating}
        >
          {submitting ? "Submitting..." : "Submit"}
        </Button>
      </div>
    </form>
  );
};

const LearningProgramListUser = () => {

  const { hideLoader, showLoader } = useLoader();
  const navigate = useNavigate();

  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);

  const [viewLoading, setViewLoading] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [modalType, setModalType] = useState(null); // delete
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [documentPath, setDocumentPath] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    category: "",
    start_date: null,
    end_date: null
  });
  const [filterConfig, setFilterConfig] = useState([
    {
      name: "search",
      type: "text",
      label: "Program name/code",
      grid: 2,
    },
    {
      name: "category",
      type: "select",
      label: "Category",
      grid: 2,
      options: []
    },
    {
      name: "start_date",
      type: "date",
      label: "Start Date",
      grid: 2,
    },
    {
      name: "end_date",
      type: "date",
      label: "End Date",
      grid: 2,
    },
  ]);

  const fetchPrograms = async (page = currentPage, customFilters = filters) => {
    try {
      showLoader();
      const res = await api.post("/lnd/user/get-program-list-user", {
        page,
        limit: itemsPerPage,
        searchData: {
          ...customFilters,
          search: customFilters.search?.trim() || ""
        }
      });

      const responseData = res.data.data;
      const pagination = responseData?.pagination;

      setPrograms(responseData?.data || []);
      setTotalPages(pagination?.totalPages || 1);
      setTotalItems(pagination?.total || 0);
      setDocumentPath(responseData?.documentPath);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch programs");
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  useEffect(() => {
    fetchPrograms(currentPage, filters);
  }, [currentPage]);

  const fetchCategories = async () => {
    try {
      const res = await api.get("/lnd/get-category-dropdown");
      const categoryList = res.data.data.data || [];

      const categoryOptions = categoryList.map(cat => ({
        label: cat.title,
        value: cat.id
      }));

      setFilterConfig(prev =>
        prev.map(field =>
          field.name === "category"
            ? { ...field, options: categoryOptions }
            : field
        )
      );

    } catch (err) {
      toast.error("Failed to load categories");
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const closeModal = () => {
    setModalType(null);
    setSelectedProgram(null);
  };

  const handleDeleteProgram = async () => {
    try {
      await api.delete(`/lnd/delete-program/${selectedProgram.id}`);
      toast.success("Program deleted");
      closeModal();
      fetchPrograms(currentPage, filters);
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const handleUpdate = () => {
    fetchPrograms(currentPage, filters);
  }

  const { showModal, hideModal } = useModal();

  const handleOpenFeedback = (programId) => {
    showModal(
      "Submit Feedback",
      <FeedbackForm programId={programId} onSubmit={hideModal} handleUpdate={handleUpdate} />,
      "sm"
    );
  };

  const handleCertficateOpen = (key, title, certificate_img = "") => {
    let body;

    if (key === "certificate") {
      body = (
        <div className="text-center">
          {certificate_img ? (
            <img
              src={BASE_URL + documentPath + certificate_img}
              alt="Certificate"
              style={{
                maxWidth: "100%",
                maxHeight: "500px",
                borderRadius: "8px"
              }}
            />
          ) : (
            <p>No certificate available</p>
          )}
        </div>
      );
    }

    showModal(title, body);
  };

  const openViewModal = async (id) => {
      let title = "View Program Details"
      let body;
      let program_detail = {};
  
      try {
        setViewLoading(true);
  
        const res = await api.get(`/lnd/${id}/preview`);
        program_detail = res.data?.data?.program;
  
      } catch (err) {
        toast.error("Failed to load program");
      } finally {
        setViewLoading(false);
      }
  
      body = (
        <>
          {viewLoading ? (
            <p className="text-center py-4">Loading...</p>
          ) : !program_detail ? (
            <p className="text-center py-4 text-muted">No data found</p>
          ) : (
            <div style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: "5px" }}>
  
              {/* BASIC INFO CARD */}
              <div className="card shadow-sm mb-3 border-0">
                <div className="card-body">
                  <h6 className="fw-bold mb-3 text-primary">Basic Information</h6>
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <small className="text-muted">Name</small>
                      <div>{program_detail.program_name}</div>
                    </div>
                    <div className="col-md-6 mb-2">
                      <small className="text-muted">Code</small>
                      <div>{program_detail.program_code}</div>
                    </div>
                    <div className="col-md-6 mb-2">
                      <small className="text-muted">Mode</small>
                      <div>{program_detail.learning_mode}</div>
                    </div>
                    <div className="col-12">
                      <small className="text-muted">Description</small>
                      <div>{stripHtml(program_detail.description)}</div>
                    </div>
                  </div>
                </div>
              </div>
  
              {/* SCHEDULE CARD */}
              <div className="card shadow-sm mb-3 border-0">
                <div className="card-body">
                  <h6 className="fw-bold mb-3 text-primary">Schedule</h6>
                  <div className="row">
                    <div className="col-md-4 mb-2">
                      <small className="text-muted">Duration</small>
                      <div>{program_detail.duration_hours} hrs</div>
                    </div>
                    <div className="col-md-4 mb-2">
                      <small className="text-muted">Cost</small>
                      <div>₹{program_detail.estimated_cost}</div>
                    </div>
                    <div className="col-md-4 mb-2">
                      <small className="text-muted">Certification</small>
                      <div>{program_detail.is_certified ? "Yes" : "No"}</div>
                    </div>
                  </div>
                </div>
              </div>
  
              {/* SKILLS CARD */}
              <div className="card shadow-sm mb-3 border-0">
                <div className="card-body">
                  <h6 className="fw-bold mb-3 text-primary">Skill Outcomes</h6>
  
                  {program_detail.skill_outcomes?.length ? (
                    program_detail.skill_outcomes.map((s, i) => (
                      <div
                        key={i}
                        className="p-3 mb-2 rounded"
                        style={{ background: "#f8f9fa" }}
                      >
                        <div><strong>Skill:</strong> {s?.skill?._Stitle || "N/A"}</div>
                        <div><strong>Skill Level:</strong> {s.target_level}</div>
                        <div><strong>Proficiency Level:</strong> {s.rating_gain}</div>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted">No skills available</p>
                  )}
                </div>
              </div>
  
            </div>
          )}
        </>
      );
  
      showModal(title, body, "md");
    };

  const columns = [
    {
      label: "Program Code",
      render: (row) => (
        <>
          {row.program.program_code}
        </>
      ),
      className: "fw-semibold",
    },
    {
      label: "Program Name",
      render: (row) => (
        <>
          {row.program.program_name}
        </>
      ),
    },
    {
      label: "Start Date",
      render: (row) => defaultDateFormatter(row.start_date) || "N/A",
    },
    {
      label: "End Date",
      render: (row) => defaultDateFormatter(row.end_date) || "N/A",
    },
    {
      label: "Category",
      render: (row) => row.program.category.title || "N/A",
    },
    {
      label: "Completion(%)",
      render: (row) => `${row.progress_percentage ?? 0}%`,
    },
    {
      label: "Status",
      render: (row) => {
        const statusClass =
          row.status === "completed"
            ? "text-success"
            : row.status === "in_progress"
              ? "text-warning"
              : "text-secondary";

        const statusText =
          row.status === "completed"
            ? "Completed"
            : row.status === "in_progress"
              ? "In Progress"
              : "Not Started";

        return <span className={statusClass}>{statusText}</span>;
      },
    },
    {
      label: "View Certificate",
      render: (row) => {
        if (row.status === "completed" && row?.certificate_img) {
          return (
            <div>

              <Tooltip title="View" placement="top" arrow>
                <RemoveRedEyeIcon
                  className="cursor-pointer"
                  sx={{ color: "black", marginLeft: "8px" }}
                  onClick={() => handleCertficateOpen("certificate", "Certificate", row?.certificate_img)}
                />
              </Tooltip>
            </div>
          );
        }
        return "--";
      }
    },
    {
      label: "Actions",
      render: (row) => {
        {
          (row.status === "completed") &&
            (<div>
              <Tooltip title="Send Feedback" placement="top" arrow>
                <div
                  className="cursor-pointer"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title="Send Feedback"
                  onClick={() => handleOpenFeedback(row.program_id)}
                >
                  <SendFill size={16} />
                </div>
              </Tooltip>
            </div>
            )
        }

        return (
          <div className="d-flex gap-2">
            <div
              className="cursor-pointer"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="View Program"
              onClick={() => openViewModal(row.program_id)}
            >
              <Eye size={16} />
            </div>

            <div
              className={"cursor-pointer"}
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Learn Tutorial"
              onClick={() => {
                navigate(
                  `/learning-and-development/user-learning-program/tutorial/${row.program_id}/dashboard`
                );
              }}
            >
              <PlayCircleOutlineIcon style={{ fontSize: 18 }} />
            </div>

            {(row.status === "completed") && (!row.is_feedback_submitted) &&
              (<div>
                <Tooltip title="Send Feedback" placement="top" arrow>
                  <div
                    className="cursor-pointer"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title="View All Employees"
                    onClick={() => handleOpenFeedback(row.program_id)}
                  >
                    <SendFill size={16} />
                  </div>
                </Tooltip>
              </div>
              )}
          </div>
        );
      },
    }
  ];

  return (
    <div className="container-fluid py-4">

      {/* HEADER */}

      <div className="row g-2 align-items-end mb-4">

        {/* FILTER SECTION */}
        <div className="col-12 col-lg">
          <CommonFilter
            config={filterConfig}
            defaultValues={{
              search: "",
              start_date: null,
              end_date: null,
              status: "",
              category: ""
            }}
            onSubmit={(data) => {
              setFilters(data);
              setCurrentPage(1);
              fetchPrograms(1, data);
            }}
          // onExport={handleExport}
          />
        </div>

      </div>


      <Outlet />

      {/* TABLE */}
      <DynamicTable
        columns={columns}
        data={programs}
        rowKey="id"
        loading={loading}
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        onPageChange={(page) => setCurrentPage(page)}
      />

      {modalType === "delete" && (
        <ModalWrapper onClose={closeModal}>
          <h5 className="fw-bold mb-3">Delete Program</h5>

          <p>
            Are you sure you want to delete{" "}
            <strong>{selectedProgram?.program_name}</strong>?
          </p>

          <div className="d-flex gap-2">
            <button
              className="btn btn-danger flex-fill"
              onClick={handleDeleteProgram}
            >
              Delete
            </button>

            <button
              className="btn btn-outline-secondary flex-fill"
              onClick={closeModal}
            >
              Cancel
            </button>
          </div>
        </ModalWrapper>
      )}

    </div>

  );
};

export default LearningProgramListUser;